import Back from '@/components/Back';
import styles from './index.module.less';
import { Button, Flex, message, Space, Spin, Tag, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { companyApi, ETaskSource } from '@/axios/company';
import { useRequest } from 'ahooks';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import MonitorTable from '../MonitorTable';
import CommonModalFactory from '@/plugins/ModalFactory';
import SearchWord from '../SearchWord';
import Article from '../Article';
import AnalysisTable from '../AnalysisTable';
import { useStore } from '@/stores';
import IntervalMonitorTable from '../IntervalMonitorTable';
import CompanyInformation from '../Information';
import AccessButton from '@/components/AccessButton';
import { contains } from '@/utils';
import { observer } from 'mobx-react';
import DataDashboard from '../DataDashboard';
import { getTemplateList, TemplatePageItem } from '@/axios/geo';
import CompanyWebsite from '../Website';
import EnterpriseAccount from '../EnterpriseAccount';
import { openEditCompanyModal } from '../Edit';
import { userApi } from '@/axios/user';
import dayjs from 'dayjs';

enum ESubMenuKey {
  MonitorTask = 'monitorTask',
  SearchWord = 'searchWord',
  EnterpriseAccount = 'enterpriseAccount',
  AnalysisTask = 'analysisTask',
  DataBoard = 'dataBoard',
  ArticleManage = 'articleManage',
  CompanyInfo = 'companyInfo',
  /* 企业官网 */
  CompanyWebsite = 'companyWebsite',
}
const subMenus: {
  key: ESubMenuKey;
  label: string;
  permission?: string[];
}[] = [
  {
    key: ESubMenuKey.SearchWord,
    label: '搜索词管理',
    permission: ['company:search_word:view'],
  },
  {
    key: ESubMenuKey.MonitorTask,
    label: '监控任务',
    permission: ['company:task:view'],
  },
  {
    key: ESubMenuKey.DataBoard,
    label: '数据看板',
    permission: ['company:data_dashboard:view'],
  },
  {
    key: ESubMenuKey.AnalysisTask,
    label: '分析报告',
    permission: ['company:analysis_task:view'],
  },
  {
    key: ESubMenuKey.ArticleManage,
    label: '文章管理',
    permission: ['company:article:view'],
  },
  {
    key: ESubMenuKey.CompanyInfo,
    label: '企业资料',
    permission: ['company:information:view'],
  },
  {
    key: ESubMenuKey.CompanyWebsite,
    label: '企业官网',
    permission: ['company:official_site'],
  },
  {
    key: ESubMenuKey.EnterpriseAccount,
    label: '企业账号',
    permission: ['company:view'],
  },
];

const companyStatusMap: Record<string, { label: string; color: string }> = {
  DELIVERING: {
    label: '首次交付 - 交付中',
    color: 'processing',
  },
  FIRST_WAIT_ACCEPTANCE: {
    label: '首次交付 - 待验收',
    color: 'warning',
  },
  FIRST_OVERDUE: {
    label: '首次交付 - 已超期',
    color: 'error',
  },
  ONLINE: {
    label: '首次交付 - 已上线',
    color: 'success',
  },
  FIRST_ONLINE_OPTIMIZING: {
    label: '首次交付 - 已上线+继续优化',
    color: 'cyan',
  },
  SUPPLEMENT_DELIVERING: {
    label: '补词优化 - 交付中',
    color: 'processing',
  },
  SUPPLEMENT_WAIT_ACCEPTANCE: {
    label: '补词优化 - 待验收',
    color: 'warning',
  },
  SUPPLEMENT_NOT_STANDARD: {
    label: '补词优化 - 未达标',
    color: 'error',
  },
  SUPPLEMENT_OPTIMIZED: {
    label: '补词优化 - 已优化',
    color: 'success',
  },
  TESTING: {
    label: '测试',
    color: 'warning',
  },
  STOPPED: {
    label: '停止交付',
    color: 'error',
  },
};

const formatAccountDisplay = (
  accountInfo?: {
    nickName?: string;
    nickname?: string;
    email?: string;
  } | null
) => {
  const nickname = accountInfo?.nickName || accountInfo?.nickname || '';
  const email = accountInfo?.email || '';
  if (!nickname) {
    return '-';
  }
  if (!email) {
    return nickname;
  }
  return <Tooltip title={email}>{nickname}</Tooltip>;
};

const getCompanyStatusTag = (status?: string, statusDesc?: string) => {
  if (!status && !statusDesc) {
    return '-';
  }
  const statusConfig = status ? companyStatusMap[status] : undefined;
  const label = statusDesc || statusConfig?.label || '-';
  const color = statusConfig?.color;
  return <Tag color={color}>{label}</Tag>;
};

const CompanyDetail = ({ onReturn }: { onReturn?: () => void }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const companyId = searchParams.get('companyId');
  const userStore = useStore('user');
  const {
    data: companyInfo,
    loading: companyInfoLoading,
    runAsync: refreshCompany,
  } = useRequest(
    () => {
      return companyApi.get(companyId!);
    },
    {
      refreshDeps: [companyId],
    }
  );
  const [activeTab, setActiveTab] = useState<ESubMenuKey>();
  useEffect(() => {
    // 检查权限，默认选中第一个有权限的tab
    for (const menu of subMenus) {
      if (contains(userStore.info?.permissions || [], menu.permission || [])) {
        setActiveTab(menu.key);
        break;
      }
    }
  }, [userStore.info?.permissions]);
  const renderCompanyInfo = () => {
    if (!companyInfo) return null;
    const statusTag = getCompanyStatusTag(companyInfo.status, companyInfo.statusDesc);
    const providerAccount = formatAccountDisplay(companyInfo.providerInfo);
    const agentAccount = formatAccountDisplay(companyInfo.agentInfo);
    const expireTimeText = companyInfo.expireTime ? dayjs(companyInfo.expireTime).format('YYYY-MM-DD') : '-';
    const remarkText = companyInfo.remark || '-';
    const infoList: { title: string; value: string | number | JSX.Element; span?: 1 | 2 }[] = [
      {
        title: '公司名称',
        value: companyInfo.companyName,
      },
      {
        title: '企业状态',
        value: statusTag,
      },
      {
        title: '品牌',
        value: companyInfo.brand,
      },
      {
        title: '套餐类型',
        value: companyInfo.combo || '',
      },
      {
        title: '服务商账号',
        value: providerAccount,
      },
      {
        title: '代理商账号',
        value: agentAccount,
      },
      {
        title: '到期时间',
        value: expireTimeText,
        span: 2,
      },
      {
        title: '备注',
        value: remarkText,
        span: 2,
      },
    ];
    return (
      <Spin spinning={companyInfoLoading}>
        <div className={styles.company_info_grid}>
          {infoList.map((item) => {
            return (
              <div
                className={item.span === 2 ? `${styles.company_info_item} ${styles.company_info_item_full}` : styles.company_info_item}
                key={item.title}
              >
                <span className={styles.company_info_label}>{item.title}：</span>
                <span className={styles.company_info_value}>{item.value}</span>
              </div>
            );
          })}
        </div>
      </Spin>
    );
  };
  const renderTabs = () => {
    return (
      <Flex gap={24}>
        {subMenus
          .filter((menu) => {
            return contains(userStore.info?.permissions || [], menu.permission || []);
          })
          .map((menu) => {
            return (
              <div
                key={menu.key}
                className={classNames(styles['detail_tab_item'], {
                  [styles['detail_tab_item_active']]: activeTab === menu.key,
                })}
                onClick={() => {
                  setActiveTab(menu.key);
                }}
              >
                {menu.label}
              </div>
            );
          })}
      </Flex>
    );
  };
  const renderContent = () => {
    switch (activeTab) {
      case ESubMenuKey.MonitorTask:
        return (
          <Flex vertical gap={8}>
            <AccessButton permission={['company:task:schedule:view']}>
              <IntervalMonitorTable companyInfo={companyInfo} refreshCompanyInfo={refreshCompany} />
            </AccessButton>
            <AccessButton permission={['company:task:view:page']}>
              <MonitorTable />
             </AccessButton>
          </Flex>
        );
      case ESubMenuKey.SearchWord:
        return <SearchWord companyId={new URLSearchParams(window.location.search).get('companyId') || ''} />;
      case ESubMenuKey.EnterpriseAccount:
        return <EnterpriseAccount companyId={new URLSearchParams(window.location.search).get('companyId') || ''} />;
      case ESubMenuKey.AnalysisTask:
        return <AnalysisTable companyInfo={companyInfo} />;
      case ESubMenuKey.DataBoard:
        return <DataDashboard />;
      case ESubMenuKey.ArticleManage:
        return <Article companyInfo={companyInfo} />;
      case ESubMenuKey.CompanyInfo:
        return <CompanyInformation companyInfo={companyInfo} refreshCompanyInfo={refreshCompany} />;
      case ESubMenuKey.CompanyWebsite:
        return <CompanyWebsite companyInfo={companyInfo} refreshCompanyInfo={refreshCompany} />;
      default:
        return null;
    }
  };
  return (
    <div className={styles.detail}>
      <Back
        text={
          <Flex
            align="center"
            gap={8}
            style={{
              fontSize: 20,
            }}
          >
            <span>{companyInfo?.companyName}</span>
            <AccessButton permission={['company:edit']}>
              <EditOutlined
                onClick={(e) => {
                  e.stopPropagation();
                  if (!companyInfo?.companyId) {
                    return;
                  }
                  openEditCompanyModal({ id: companyInfo.companyId }, { title: '编辑企业' }, refreshCompany);
                }}
              />
            </AccessButton>
          </Flex>
        }
        onClick={onReturn}
      />
      <Flex justify="space-between">
        {renderCompanyInfo()}
        <Space>
          <AccessButton permission={['company:view:client:ticket']}>
            <Button
              type="primary"
              onClick={async () => {
                const newTab = window.open('', '_blank');
                try {
                  if (!companyId) {
                    newTab?.close();
                    message.warning('未获取到企业ID');
                    return;
                  }
                  const res = await userApi.getClientSsoTicket({ companyId });
                  if (!res?.redirectUrl) {
                    newTab?.close();
                    message.warning('未获取到客户后台跳转地址');
                    return;
                  }
                  if (newTab) {
                    newTab.location.href = res.redirectUrl;
                  } else {
                    window.open(res.redirectUrl, '_blank');
                  }
                } catch (error) {
                  newTab?.close();
                  throw error;
                }
              }}
            >
              查看客户后台
            </Button>
          </AccessButton>
          <AccessButton permission={['company:view:report']}>
            <Button
              type="primary"
              onClick={() => {
                // window.open(`/geoReport/${companyInfo?.companyId}?source=${ETaskSource.Company}`);
                openTemplateModal(
                  {
                    recordId: companyInfo?.companyId,
                    source: ETaskSource.Company,
                  },
                  undefined,
                  undefined
                );
              }}
            >
              查看客户报表
            </Button>
          </AccessButton>
          {/* <Button
            type="primary"
            onClick={() => {
              openUpdateReportDataModal(
                { companyId, selected: companyInfo?.recordIds, platformList: appStore.platformList },
                undefined,
                () => {
                  message.success('更新成功');
                  refreshCompany();
                }
              );
            }}
          >
            更新客户报表
          </Button> */}
        </Space>
      </Flex>
      {renderTabs()}
      {renderContent()}
    </div>
  );
};
export default observer(CompanyDetail);

/** 打开选择报表模板的弹窗 */
export const openTemplateModal = CommonModalFactory<{
  recordId: string;
  templateList: TemplatePageItem[];
  source: ETaskSource;
}>(
  {
    title: '查看报表',
    width: 600,
  },
  ({ data, setOkFn }) => {
    const { recordId, templateList, source } = data as {
      recordId: string;
      templateList: TemplatePageItem[];
      source: ETaskSource;
    };
    const [templateId, setTemplateId] = useState<string>(templateList?.[0]?.templateId || '');
    useEffect(() => {
      setOkFn(() => {
        const { endpoint = '' } = templateList.find((el) => el.templateId === templateId) || {};
        const isLocal = process.env.NODE_ENV === 'development';
        if (isLocal) {
          if (source === ETaskSource.Company) {
            window.open(`/geoReport/${recordId}?templateId=${templateId}&source=${source}`);
          } else {
            window.open(`/geoReport/${recordId}?recordId=${recordId}&templateId=${templateId}&source=${source}`);
          }
        } else {
          if (source === ETaskSource.Company) {
            window.open(`${endpoint}/geoReport/${recordId}?templateId=${templateId}&source=${source}`);
          } else {
            window.open(
              `${endpoint}/geoReport/${recordId}?recordId=${recordId}&templateId=${templateId}&source=${source}`
            );
          }
        }
      });
    }, [recordId, templateId, templateList]);
    return (
      <div>
        请选择使用的看板模板
        <div className={styles['geo_report_template_list']}>
          {templateList?.map((template) => {
            return (
              <div
                key={template.templateId}
                className={classNames(styles['geo_report_template_item'], {
                  [styles['geo_report_template_item_selected']]: templateId === template.templateId,
                })}
                onClick={() => {
                  setTemplateId(template.templateId);
                }}
              >
                {template.companyName}
              </div>
            );
          })}
        </div>
      </div>
    );
  },
  async (props) => {
    const res = await getTemplateList({ pageIndex: 1, pageSize: 100, pageType: 'REPORT' });
    return {
      ...props,
      templateId: res.records?.[0]?.templateId,
      templateList: res.records,
    };
  }
);
